import React, { memo, useId } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import Checkbox from '@lib/components/CheckBox/CheckBox';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import useOrdering from '@lib/hooks/useOrdering';
import { BaseItem, TableProps } from '@lib/components/Table/types';
import { GetUrlParams } from '@lib/enums/urls';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import TableHeadCell from '@lib/components/Table/components/TableHead/TableHeadCell';
import styles from './TableHead.module.scss';

type Props = {
  hasActionsColumn: boolean;
  hasActionsMenuColumn: boolean;
  hasSelectColumn: boolean;
  isSelectAllCheckboxChecked: boolean;
  isSelectAllCheckboxIndeterminate: boolean;
  isSelectAllDisabledByLimit?: boolean;
  selectionLimit?: number;
};

function TableHead<T extends BaseItem>(props: TableProps<T> & Props) {
  const {
    borderedCells,
    columns,
    hasActionsColumn,
    hasActionsMenuColumn,
    hasSelectColumn,
    isSelectAllCheckboxChecked,
    isSelectAllCheckboxIndeterminate,
    isSelectAllDisabledByLimit,
    radioSelect,
    selectionLimit,
    onSortChange,
    orderingDirectionKey = GetUrlParams.OrderingDirection,
    orderingFieldKey = GetUrlParams.OrderingField,
    orderingState,
    tree,
    useBulkSelectResult = {} as UseBulkSelectResult<T>,
  } = props;
  const { t } = useTranslation();
  const { handleSelectAllTableItems, handleUnselectAllTableItems } =
    useBulkSelectResult;
  // Unique per Table instance so label `htmlFor` can never resolve to a
  // sibling table's select-all input (e.g. DocumentsModalTableMultiselect
  // opened on top of the SiteAreas list page).
  const selectAllUid = useId();
  const selectAllCheckboxId = `table-head-checkbox-${selectAllUid}`;

  const { orderingDirection, orderingField, onOrderingChange } = useOrdering({
    onSortChange,
    orderingDirectionKey,
    orderingFieldKey,
    orderingState,
  });

  const handleSelectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!hasSelectColumn || isSelectAllDisabledByLimit) return;
    if (isSelectAllCheckboxIndeterminate) {
      handleUnselectAllTableItems();
    } else if (event.target.checked) {
      handleSelectAllTableItems();
    } else {
      handleUnselectAllTableItems();
    }
  };

  return (
    <thead
      key={columns.length}
      className={classNames(styles.head, {
        [styles.tableWithBorderedCells]: borderedCells,
      })}
    >
      <tr data-test-id="table__header-row">
        {hasSelectColumn && (
          <th className={styles.selectCell}>
            {/* A radio group has no "select all" — exactly one row can ever
                be selected, so the header control is dropped entirely.
                A hierarchical tree view has no "select all" either —
                collapsed/unloaded descendants aren't in `data`, so bulk-
                selecting everything isn't a meaningful action there. Once
                search/filter flattens the tree into a complete match list
                (isFlatView), the rendered rows are the whole result set
                again, so the control comes back. */}
            {!radioSelect && (!tree || tree.isFlatView) && (
              <Tooltip
                body={t('selection-limit-reached', { limit: selectionLimit })}
                disabled={!isSelectAllDisabledByLimit}
                placement="bottomLeft"
              >
                <Checkbox
                  id={selectAllCheckboxId}
                  name={selectAllCheckboxId}
                  checked={isSelectAllCheckboxChecked}
                  indeterminate={isSelectAllCheckboxIndeterminate}
                  onChange={handleSelectedChange}
                  disabled={isSelectAllDisabledByLimit}
                  wrapperClassName={styles.selectCellCheckbox}
                  data-test-id="table__select-all-checkbox"
                />
              </Tooltip>
            )}
          </th>
        )}
        {columns.map((column, index) => (
          <TableHeadCell
            key={column?.id || uuidv4()}
            column={column}
            index={index}
            onOrderingChange={onOrderingChange}
            orderingDirection={orderingDirection}
            orderingField={orderingField}
          />
        ))}
        {(hasActionsColumn || hasActionsMenuColumn) && (
          <th className={styles.actionsCell}> </th>
        )}
      </tr>
    </thead>
  );
}

export default memo(TableHead) as <T extends BaseItem>(
  props: TableProps<T> & Props,
) => React.ReactElement;
